import { differenceInCalendarDays, parseISO } from 'date-fns';

const WAREHOUSE_STATUS_ID = 3;
const BUSINESS_TIMEZONE = 'Europe/Warsaw';
const MAX_TRANSIT_DAY_OFFSET = 4;

const businessDateFormatter = new Intl.DateTimeFormat('en', {
  timeZone: BUSINESS_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function getBusinessDayStart(value) {
  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const parts = businessDateFormatter.formatToParts(date);
  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  const day = parts.find((part) => part.type === 'day')?.value;

  if (!year || !month || !day) {
    return null;
  }

  return parseISO(`${year}-${month}-${day}`);
}

export const getDaysUntilPickupAt = (order, now = new Date()) => {
  if (order?.statusId === WAREHOUSE_STATUS_ID || !order?.pickupAt) {
    return null;
  }

  const pickupAt = getBusinessDayStart(order.pickupAt);
  const today = getBusinessDayStart(now);

  if (!pickupAt || !today) {
    return null;
  }

  return differenceInCalendarDays(pickupAt, today);
};

export const getTransitCounterLevel = (order, now = new Date()) => {
  const daysUntilPickupAt = getDaysUntilPickupAt(order, now);

  if (daysUntilPickupAt === null || daysUntilPickupAt < 0 || daysUntilPickupAt > MAX_TRANSIT_DAY_OFFSET) {
    return null;
  }

  return Math.max(1, 4 - daysUntilPickupAt);
};

export const getTransitDisplayLabel = (order, now = new Date()) => {
  const daysUntilPickupAt = getDaysUntilPickupAt(order, now);

  if (daysUntilPickupAt === null || daysUntilPickupAt < 0 || daysUntilPickupAt > MAX_TRANSIT_DAY_OFFSET) {
    return '';
  }

  return `+${daysUntilPickupAt}`;
};

/**
 * Categorise a transit (pre-advice) order by ITS pickup day, relative to the
 * business "today" (Europe/Warsaw). Drives the day-aware marker styling and the
 * "Transit: today/tomorrow/in N days" InfoWindow line, and the transit-day filter.
 *
 *   'today'    -> arrives today      (0 days to pickup)
 *   'tomorrow' -> arrives tomorrow   (1 day)
 *   'later'    -> arrives in 2-4 days (the counter still shows it)
 *   null       -> not a counted transit order (warehouse, no pickupAt, past, >4d)
 *
 * Kept in lock-step with getTransitCounterLevel: same null cases, same horizon.
 */
export const getTransitDayCategory = (order, now = new Date()) => {
  const daysUntilPickupAt = getDaysUntilPickupAt(order, now);

  if (daysUntilPickupAt === null || daysUntilPickupAt < 0 || daysUntilPickupAt > MAX_TRANSIT_DAY_OFFSET) {
    return null;
  }

  if (daysUntilPickupAt === 0) return 'today';
  if (daysUntilPickupAt === 1) return 'tomorrow';
  return 'later';
};
